import React, { useState, useEffect, useRef } from "react";
import { FiArrowRight, FiLink, FiZap } from "react-icons/fi";
import { LuPointer } from "react-icons/lu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function RadialOrbitalTimeline({ timelineData }) {
  const [expandedItems, setExpandedItems] = useState({});
  const [viewMode, setViewMode] = useState("orbital");
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [pulseEffect, setPulseEffect] = useState({});
  const [centerOffset, setCenterOffset] = useState({ x: 0, y: 0 });
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [radius, setRadius] = useState(280);

  const containerRef = useRef(null);
  const orbitRef = useRef(null);
  const nodeRefs = useRef({});

  useEffect(() => {
    // Increased radius for both mobile and desktop
    const handleResize = () => setRadius(window.innerWidth < 640 ? 150 : 280);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleContainerClick = (e) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const getRelatedItems = (itemId) => {
    const currentItem = timelineData.find((item) => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  };

  const centerViewOnNode = (nodeId) => {
    if (viewMode !== "orbital" || !nodeRefs.current[nodeId]) return;
    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const totalNodes = timelineData.length;
    const targetAngle = (nodeIndex / totalNodes) * 360;
    setRotationAngle(270 - targetAngle);
  };

  const toggleItem = (id) => {
    setExpandedItems((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (parseInt(key) !== id) newState[parseInt(key)] = false;
      });

      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);
        const relatedItems = getRelatedItems(id);
        const newPulseEffect = {};
        relatedItems.forEach((relId) => {
          newPulseEffect[relId] = true;
        });
        setPulseEffect(newPulseEffect);
        centerViewOnNode(id);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }
      return newState;
    });
  };

  useEffect(() => {
    let rotationTimer;
    if (autoRotate && viewMode === "orbital") {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => {
          const newAngle = (prev + 0.15) % 360; // Slightly slower rotation for larger scale
          return Number(newAngle.toFixed(3));
        });
      }, 50);
    }
    return () => {
      if (rotationTimer) clearInterval(rotationTimer);
    };
  }, [autoRotate, viewMode]);

  const calculateNodePosition = (index, total) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radian = (angle * Math.PI) / 180;
    const x = radius * Math.cos(radian) + centerOffset.x;
    const y = radius * Math.sin(radian) + centerOffset.y;
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(0.4, Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2)));
    return { x, y, angle, zIndex, opacity };
  };

  const isRelatedToActive = (itemId) => {
    if (!activeNodeId) return false;
    const relatedItems = getRelatedItems(activeNodeId);
    return relatedItems.includes(itemId);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "completed": return "text-white bg-brand-darkSage border-brand-darkSage";
      case "in-progress": return "text-white bg-brand-gold border-brand-gold";
      case "pending": return "text-brand-darkSage bg-brand-sand border-brand-darkSage/20";
      default: return "text-brand-darkSage bg-brand-sand border-brand-darkSage/20";
    }
  };

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center bg-transparent overflow-hidden"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
        <div
          className="absolute w-full h-full flex items-center justify-center"
          ref={orbitRef}
          style={{
            perspective: "1000px",
            transform: `translate(${centerOffset.x}px, ${centerOffset.y}px)`,
          }}
        >
          {/* Center Orb - Slightly Larger */}
          <div className="absolute w-16 h-16 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-brand-gold via-brand-sage to-brand-darkSage animate-pulse flex items-center justify-center z-10 shadow-[0_0_40px_rgba(212,175,55,0.4)]">
            <div className="absolute w-20 h-20 md:w-32 md:h-32 rounded-full border border-brand-gold/30 animate-ping opacity-70"></div>
            <div className="absolute w-24 h-24 md:w-40 md:h-40 rounded-full border border-brand-sage/20 animate-ping opacity-50" style={{ animationDelay: "0.5s" }}></div>
            <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-white/90 backdrop-blur-md"></div>
          </div>

          {/* Orbit Paths */}
          <div className="absolute rounded-full border border-brand-darkSage/10 border-dashed" style={{ width: radius * 2, height: radius * 2 }}></div>
          <div className="absolute rounded-full border border-brand-darkSage/5" style={{ width: radius * 3, height: radius * 3 }}></div>

          {/* Nodes */}
          {timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length);
            const isExpanded = expandedItems[item.id];
            const isRelated = isRelatedToActive(item.id);
            const isPulsing = pulseEffect[item.id];
            const Icon = item.icon;

            const nodeStyle = {
              transform: `translate(${position.x}px, ${position.y}px)`,
              zIndex: isExpanded ? 200 : position.zIndex,
              opacity: isExpanded ? 1 : position.opacity,
            };

            return (
              <div
                key={item.id}
                ref={(el) => (nodeRefs.current[item.id] = el)}
                className="absolute transition-all duration-700 cursor-pointer flex items-center justify-center"
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >
                {/* Energy Aura (Centered cleanly) */}
                <div
                  className={`absolute rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${isPulsing ? "animate-pulse duration-1000" : ""}`}
                  style={{
                    background: `radial-gradient(circle, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0) 70%)`,
                    width: `${item.energy * 0.8 + 60}px`,
                    height: `${item.energy * 0.8 + 60}px`,
                  }}
                ></div>

                {/* Node Icon - Larger Size */}
                <div
                  className={`w-12 h-12 md:w-16 md:h-16 relative rounded-full flex items-center justify-center border-2 transition-all duration-300 transform
                    ${isExpanded 
                      ? "bg-brand-darkSage text-white border-brand-gold shadow-lg shadow-brand-gold/30 scale-150" 
                      : isRelated 
                      ? "bg-brand-sage/20 text-brand-darkSage border-brand-gold animate-pulse" 
                      : "bg-white text-brand-darkSage border-brand-darkSage/20 hover:border-brand-gold/50"
                    }
                  `}
                >
                  <Icon className="w-5 h-5 md:w-6 md:h-6" />
                </div>

                {/* Node Title & Phase (when not expanded) */}
                <div
                  className={`absolute top-14 md:top-20 flex flex-col items-center whitespace-nowrap transition-all duration-300 left-1/2 -translate-x-1/2 ${
                    isExpanded ? "opacity-0 invisible" : "opacity-100"
                  }`}
                >
                  <span className="text-sm md:text-base font-serif tracking-wider text-brand-darkSage font-bold drop-shadow-sm">
                    {item.title}
                  </span>
                  <span className="text-[10px] md:text-xs uppercase tracking-widest text-brand-darkSage/80 mt-0.5 font-semibold">
                    {item.date}
                  </span>
                  
                  {/* Updated click hint with gesture icon and better visibility */}
                  <span className="flex items-center gap-1.5 text-[10px] md:text-xs uppercase tracking-widest text-brand-darkSage font-bold mt-2 shadow-sm animate-pulse bg-brand-gold/20 border border-brand-gold/40 px-3 py-1 rounded-full backdrop-blur-md">
                    <LuPointer className="w-3 h-3 md:w-3.5 md:h-3.5 text-brand-darkSage" />
                    Explore Our Process
                  </span>
                </div>

                {/* Expanded Card - Increased Width */}
                {isExpanded && (
                  <Card className="absolute top-24 left-1/2 -translate-x-1/2 w-72 bg-white/95 backdrop-blur-xl border-brand-darkSage/20 shadow-2xl overflow-visible z-[250]">
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-brand-darkSage/30"></div>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <Badge className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${getStatusStyles(item.status)}`}>
                          {item.status === "completed" ? "Complete" : item.status === "in-progress" ? "In Progress" : "Pending"}
                        </Badge>
                        <span className="text-xs font-serif text-brand-lightText">{item.date}</span>
                      </div>
                      <CardTitle className="text-xl font-serif text-brand-darkSage mt-2">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-brand-lightText">
                      <p className="leading-relaxed">{item.content}</p>

                      <div className="mt-5 pt-4 border-t border-brand-darkSage/10">
                        <div className="flex justify-between items-center text-xs mb-2">
                          <span className="flex items-center text-brand-darkSage font-semibold uppercase tracking-wider">
                            <FiZap className="mr-1.5 text-brand-gold" /> Process
                          </span>
                          <span className="font-sans font-bold text-brand-gold">{item.energy}%</span>
                        </div>
                        <div className="w-full h-1.5 bg-brand-sand rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-brand-gold to-brand-sage transition-all duration-1000"
                            style={{ width: `${item.energy}%` }}
                          ></div>
                        </div>
                      </div>

                      {item.relatedIds.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-brand-darkSage/10">
                          <div className="flex items-center mb-3">
                            <FiLink className="text-brand-lightText mr-1.5" size={12} />
                            <h4 className="text-[10px] uppercase tracking-widest font-semibold text-brand-lightText">Connected Phases</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {item.relatedIds.map((relatedId) => {
                              const relatedItem = timelineData.find((i) => i.id === relatedId);
                              if (!relatedItem) return null;
                              return (
                                <Button
                                  key={relatedId}
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center h-7 px-3 py-0 text-[10px] rounded border-brand-darkSage/20 bg-brand-sand hover:bg-brand-darkSage hover:text-white text-brand-darkSage transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItem(relatedId);
                                  }}
                                >
                                  {relatedItem.title}
                                  <FiArrowRight size={10} className="ml-1.5 opacity-70" />
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}